import { z } from 'zod';
import { LIMITS } from '../limits.js';

/**
 * Beta bug reports — early-access feedback. A signed-in user submits a
 * description and (optionally) a screenshot they've already uploaded via the
 * normal presigned-S3 flow; the report lands in the platform moderation panel
 * for the team to triage. NOT content moderation (that's `reports.ts`) — this
 * is product-quality feedback while Bunker is in beta.
 *
 * The client only sends WHAT went wrong (+ a bit of reproduction context). The
 * server resolves the screenshot URL from the uploadId (ownership-checked) and
 * captures the user-agent, so nothing sensitive or forgeable is trusted.
 */

/** Triage lifecycle. */
export const bugReportStatusSchema = z.enum(['open', 'resolved', 'dismissed']);
export type BugReportStatus = z.infer<typeof bugReportStatusSchema>;

/** File a bug report. */
export const createBugReportSchema = z.object({
  description: z
    .string()
    .trim()
    .min(LIMITS.bugReport.descriptionMin)
    .max(LIMITS.bugReport.descriptionMax),
  /** id of an already-completed Upload (the screenshot). Server verifies it
   *  belongs to the reporter and stores its URL. */
  screenshotUploadId: z.string().cuid().optional(),
  /** Route/path where the bug was hit (reproduction context). */
  path: z.string().trim().max(512).optional(),
  /** Client build identifier, if known. */
  appVersion: z.string().trim().max(64).optional(),
});
export type CreateBugReportInput = z.infer<typeof createBugReportSchema>;

/** A moderator resolving a bug report. */
export const resolveBugReportSchema = z.object({
  status: z.enum(['resolved', 'dismissed']),
});
export type ResolveBugReportInput = z.infer<typeof resolveBugReportSchema>;

/** Queue filter for the moderation panel. */
export const bugReportQueueQuerySchema = z.object({
  status: bugReportStatusSchema.optional(),
  cursor: z.string().cuid().optional(),
});
export type BugReportQueueQuery = z.infer<typeof bugReportQueueQuerySchema>;

// ── Response shapes ─────────────────────────────────────────────────────────

export interface BugReportPublicUser {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

/** One bug report as shown in the moderation panel. */
export interface BugReportItem {
  id: string;
  description: string;
  screenshotUrl: string | null;
  path: string | null;
  appVersion: string | null;
  userAgent: string | null;
  status: BugReportStatus;
  createdAt: string;
  resolvedAt: string | null;
  reporter: BugReportPublicUser | null;
  reviewer: BugReportPublicUser | null;
}

export interface BugReportQueueResponse {
  reports: BugReportItem[];
  nextCursor: string | null;
}
